import { useEffect, useState, Fragment } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { summarizeAuditDetails, KvTable } from "@/lib/auditPretty";

function fmt(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleString(); }
  catch { return s; }
}

const ACTION_STYLE = {
  login: "bg-emerald-100 text-emerald-700",
  logout: "bg-slate-200 text-slate-700",
  login_failed: "bg-red-100 text-red-700",
  user_create: "bg-sky-100 text-sky-700",
  user_update: "bg-amber-100 text-amber-700",
  user_delete: "bg-rose-100 text-rose-700",
  password_reset: "bg-violet-100 text-violet-700",
  company_update: "bg-violet-100 text-violet-700",
};

export default function AuditLogsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [expanded, setExpanded] = useState({});
  const pageSize = 25;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const { data } = await api.get(`/audit-logs?page=${page}&page_size=${pageSize}`);
        if (cancelled) return;
        setItems(data.items || []);
        setTotal(data.total || 0);
      } catch (err) {
        if (!cancelled) toast.error(formatApiError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [page]);

  return (
    <div className="space-y-6" data-testid="audit-logs-page">
      <Card>
        <CardHeader>
          <CardTitle className="text-xl text-slate-800">Audit Logs</CardTitle>
          <CardDescription className="text-slate-500">
            Recent system activity. Click a row to see the full structured details.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-[#4FA3D1]" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="audit-logs-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Entity</TableHead>
                    <TableHead>IP</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((it) => {
                    const isOpen = !!expanded[it.id];
                    return (
                      <Fragment key={it.id}>
                        <TableRow
                          onClick={() => setExpanded((s) => ({ ...s, [it.id]: !isOpen }))}
                          className="cursor-pointer hover:bg-slate-50"
                          data-testid={`audit-row-${it.id}`}
                        >
                          <TableCell className="whitespace-nowrap text-slate-600">{fmt(it.timestamp)}</TableCell>
                          <TableCell>
                            <Badge className={`${ACTION_STYLE[it.action] || "bg-slate-100 text-slate-700"} hover:bg-inherit`}>
                              {it.action}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-slate-700">{it.user_email || "—"}</TableCell>
                          <TableCell className="text-slate-600">{it.entity_type}</TableCell>
                          <TableCell className="text-slate-500">{it.ip || "—"}</TableCell>
                          <TableCell className="max-w-xs">
                            <span className="text-xs text-slate-600 truncate block">
                              {summarizeAuditDetails(it.action, it.details, it.user_email)}
                            </span>
                          </TableCell>
                        </TableRow>
                        {isOpen && (
                          <TableRow data-testid={`audit-row-detail-${it.id}`}>
                            <TableCell colSpan={6} className="bg-slate-50 border-t border-slate-200">
                              <div className="text-xs text-slate-500 mb-2">
                                Entity ID: <span className="text-slate-700 font-mono">{it.entity_id || "—"}</span>
                              </div>
                              <KvTable details={it.details} />
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          <div className="mt-4 flex items-center justify-between text-sm text-slate-600">
            <span>Page {page} of {totalPages}</span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                data-testid="audit-prev-page"
              >
                <ChevronLeft className="h-4 w-4 mr-1" /> Prev
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                data-testid="audit-next-page"
              >
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
